import { Injectable, signal, computed, inject } from '@angular/core';
import { PlatformApiService } from '../../features/platform/services/platform-api.service';
import { AuthService } from './auth.service';

/**
 * Plan Service
 * Manages subscription/plan-based feature access (FIRST tier of permissions)
 * This determines what features are available to the tenant based on their subscription plan
 */
@Injectable({ providedIn: 'root' })
export class PlanService {
  private readonly platformApi = inject(PlatformApiService);
  private readonly auth = inject(AuthService);

  private readonly _planFeatures = signal<{
    plan: string;
    features: string[];
    menuItems: string[];
    wizardItems: string[];
    limits: {
      maxUsers?: number | null;
      maxTasks?: number | null;
      maxStorage?: number | null;
    };
  } | null>(null);

  readonly planFeatures = this._planFeatures.asReadonly();
  readonly currentPlan = computed(() => this._planFeatures()?.plan || 'BASIC');
  readonly availableFeatures = computed(() => this._planFeatures()?.features || []);
  readonly availableMenuItems = computed(() => this._planFeatures()?.menuItems || []);
  readonly availableWizardItems = computed(() => this._planFeatures()?.wizardItems || []);

  /**
   * Check if a feature is available in the current plan
   */
  hasFeature(feature: string): boolean {
    const features = this.availableFeatures();
    return features.includes(feature.toLowerCase());
  }

  /**
   * Check if a menu item is available in the current plan
   */
  hasMenuItem(menuItem: string): boolean {
    const menuItems = this.availableMenuItems();
    const key = menuItem.toLowerCase().replace(/^\/+/, '').split('/')[0];
    return menuItems.includes(key) || key === 'dashboard' || key === 'profile' || key === 'help';
  }

  /**
   * Check if a wizard item is available in the current plan
   */
  hasWizardItem(wizardItem: string): boolean {
    const wizardItems = this.availableWizardItems();
    return wizardItems.includes(wizardItem.toLowerCase());
  }

  /**
   * Load plan features for the current tenant
   */
  loadPlanFeatures(): void {
    const tenantId = this.auth.user()?.tenantId;
    if (!tenantId) {
      // Default to BASIC plan if no tenant
      this._planFeatures.set({
        plan: 'BASIC',
        features: ['dashboard', 'tasks', 'users', 'projects', 'calendar', 'messenger'],
        menuItems: ['dashboard', 'tasks', 'users', 'projects', 'calendar', 'messenger'],
        wizardItems: ['quick-task', 'new-project'],
        limits: {
          maxUsers: 10,
          maxTasks: 1000,
          maxStorage: 10 * 1024 * 1024 * 1024,
        },
      });
      return;
    }

    this.platformApi.getTenantPlanFeatures(tenantId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this._planFeatures.set(res.data);
        }
      },
      error: (err) => {
        console.error('Failed to load plan features:', err);
        // Default to BASIC on error
        this._planFeatures.set({
          plan: 'BASIC',
          features: ['dashboard', 'tasks', 'users', 'projects', 'calendar', 'messenger'],
          menuItems: ['dashboard', 'tasks', 'users', 'projects', 'calendar', 'messenger'],
          wizardItems: ['quick-task', 'new-project'],
          limits: {
            maxUsers: 10,
            maxTasks: 1000,
            maxStorage: 10 * 1024 * 1024 * 1024,
          },
        });
      },
    });
  }

  /**
   * Check if user can access a resource based on plan
   * This is the FIRST check - before RBAC
   */
  canAccessResource(resource: string): boolean {
    const featureMap: Record<string, string> = {
      'users': 'users',
      'tasks': 'tasks',
      'projects': 'projects',
      'departments': 'departments',
      'teams': 'teams',
      'organizations': 'organizations',
      'reports': 'reports',
      'analytics': 'analytics',
      'audit': 'audit',
      'tenants': 'tenants',
      'platform': 'platform',
      'settings': 'settings',
      'configuration': 'ui-config',
      'ui-config': 'ui-config',
      'branding': 'branding',
      'backgrounds': 'backgrounds',
      'system': 'system',
      'monitoring': 'monitoring',
      'calendar': 'calendar',
      'messenger': 'messenger',
      'feed': 'feed',
      'drive': 'drive',
      'notifications': 'notifications',
      'billing': 'billing',
      'backup': 'backup',
      'api': 'api-access',
      'api-access': 'api-access',
    };

    const feature = featureMap[resource.toLowerCase()] || resource.toLowerCase();
    return this.hasFeature(feature);
  }
}


